import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import {
  useCreateRecipe,
  useDeletePhoto,
  useIngredientNames,
  useRecipe,
  useUnits,
  useUpdateRecipe,
  useUploadPhoto,
} from '@/api/hooks'
import type { RecipeInput } from '@/api/hooks'
import {
  emptyIngredientRow,
  emptyStepRow,
  newRowId,
  type IngredientRow,
  type StepRow,
} from '@/components/editorRows'
import { IngredientEditor } from '@/components/IngredientEditor'
import { PhotoField } from '@/components/PhotoField'
import { StepEditor } from '@/components/StepEditor'
import { TagPicker } from '@/components/TagPicker'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { fr } from '@/i18n/fr'
import { quantityToInput } from '@/lib/format'

interface FormState {
  title: string
  description: string
  servings: string
  prep_time_min: string
  cook_time_min: string
  source: string
  notes: string
  tags: string[]
  ingredients: IngredientRow[]
  steps: StepRow[]
}

type FieldErrors = Partial<Record<keyof FormState, string>>

const emptyForm = (): FormState => ({
  title: '',
  description: '',
  servings: '',
  prep_time_min: '',
  cook_time_min: '',
  source: '',
  notes: '',
  tags: [],
  ingredients: [emptyIngredientRow()],
  steps: [emptyStepRow()],
})

/**
 * Validation mirrors the serializer's, deliberately (F-15). Two copies of a rule is normally
 * a smell; here the alternative is a round trip to learn that a field is missing, on a form
 * long enough that the answer arrives after scrolling away from the question.
 */
function validateForm(form: FormState): FieldErrors {
  const errors: FieldErrors = {}
  const v = fr.recipeEdit.validation

  if (!form.title.trim()) errors.title = v.titleRequired
  if (!isPositiveNumber(form.servings)) errors.servings = v.servingsRequired
  if (!isNonNegativeNumber(form.prep_time_min)) errors.prep_time_min = v.prepTimeRequired
  // 0 passes here, and has to: it is the honest cook time for a salad.
  if (!isNonNegativeNumber(form.cook_time_min)) errors.cook_time_min = v.cookTimeRequired
  if (!form.ingredients.some((row) => row.name.trim())) errors.ingredients = v.ingredientsRequired
  if (!form.steps.some((row) => row.text.trim())) errors.steps = v.stepsRequired

  return errors
}

function isNonNegativeNumber(value: string): boolean {
  const parsed = Number(value.trim())
  return value.trim() !== '' && Number.isFinite(parsed) && parsed >= 0
}

function isPositiveNumber(value: string): boolean {
  return isNonNegativeNumber(value) && Number(value.trim()) > 0
}

export function RecipeEditPage() {
  const { id } = useParams<{ id: string }>()
  const isEditing = id !== undefined
  const recipeId = Number(id)

  const { data: existing } = useRecipe(isEditing ? recipeId : Number.NaN)
  const { data: units } = useUnits()
  const { data: catalog } = useIngredientNames()
  const createRecipe = useCreateRecipe()
  const updateRecipe = useUpdateRecipe(recipeId)
  const uploadPhoto = useUploadPhoto()
  const deletePhoto = useDeletePhoto()
  const navigate = useNavigate()

  const [form, setForm] = useState<FormState>(emptyForm)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [saveError, setSaveError] = useState<string | null>(null)
  const formRef = useRef<HTMLFormElement>(null)
  // The photo is not part of `FormState`: it does not travel in the recipe document and is
  // sent by its own request after the save (F-20).
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoRemoved, setPhotoRemoved] = useState(false)

  useEffect(() => {
    if (!existing) return
    setForm({
      title: existing.title,
      description: existing.description ?? '',
      servings: existing.servings != null ? String(existing.servings) : '',
      prep_time_min: existing.prep_time_min != null ? String(existing.prep_time_min) : '',
      cook_time_min: existing.cook_time_min != null ? String(existing.cook_time_min) : '',
      source: existing.source ?? '',
      notes: existing.notes ?? '',
      tags: existing.tags ?? [],
      ingredients:
        existing.ingredients && existing.ingredients.length > 0
          ? existing.ingredients.map((ingredient) => ({
              // The row ids are minted here and live only in the browser: the server has
              // never seen them and `position` still comes from the array index.
              id: newRowId(),
              // `1,5`, not `1.500` — NFR-36. The editor's formatter, not the display one:
              // a thousands separator here could not be retyped.
              quantity: quantityToInput(ingredient.quantity),
              unit: ingredient.unit ?? '',
              name: ingredient.name,
              note: ingredient.note ?? '',
              is_optional: ingredient.is_optional ?? false,
            }))
          : [emptyIngredientRow()],
      steps:
        existing.steps && existing.steps.length > 0
          ? existing.steps.map((step) => ({ id: newRowId(), text: step.text }))
          : [emptyStepRow()],
    })
  }, [existing])

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }))
    setErrors((current) => ({ ...current, [key]: undefined }))
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setSaveError(null)

    const found = validateForm(form)
    setErrors(found)
    if (Object.keys(found).length > 0) {
      // Move to the first problem rather than leaving it to be hunted for — on a phone the
      // offending field is usually off screen by the time the button is reachable.
      const first = Object.keys(found)[0]
      formRef.current?.querySelector<HTMLElement>(`[data-field="${first}"]`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
      return
    }

    const payload: RecipeInput = {
      title: form.title.trim(),
      description: form.description,
      servings: Number(form.servings),
      prep_time_min: Number(form.prep_time_min),
      cook_time_min: Number(form.cook_time_min),
      source: form.source,
      notes: form.notes,
      tags: form.tags,
      ingredients: form.ingredients
        .filter((row) => row.name.trim())
        // `id` is stripped here — it is a client-side key, not part of the document.
        .map((row) => ({
          quantity: row.quantity.trim() === '' ? null : row.quantity.trim(),
          unit: row.unit === '' ? null : row.unit,
          name: row.name.trim(),
          note: row.note,
          is_optional: row.is_optional,
        })),
      steps: form.steps
        .filter((row) => row.text.trim())
        .map((row) => ({ text: row.text.trim() })),
    }

    let recipe
    try {
      recipe = isEditing
        ? await updateRecipe.mutateAsync(payload)
        : await createRecipe.mutateAsync(payload)
    } catch (error) {
      // Without this the rejection is swallowed and the form just sits there. Now that the
      // serializer refuses incomplete recipes, a 400 is an ordinary outcome, not a bug.
      setSaveError(describeSaveError(error))
      return
    }

    // Second phase (F-20). The recipe now exists and has an id, which is the whole reason
    // the photo cannot ride along in the first request.
    let photoFailed = false
    try {
      if (photoFile) {
        await uploadPhoto.mutateAsync({ id: recipe.id, file: photoFile })
      } else if (photoRemoved && existing?.photo) {
        await deletePhoto.mutateAsync(recipe.id)
      }
    } catch {
      // The recipe is saved. Staying on the form would invite a second submit of something
      // that already succeeded, so go to the page and say what happened there instead.
      photoFailed = true
    }

    void navigate(`/recipes/${recipe.id}`, { replace: true, state: { photoFailed } })
  }

  const isSaving =
    createRecipe.isPending ||
    updateRecipe.isPending ||
    uploadPhoto.isPending ||
    deletePhoto.isPending

  // The server decides who may write (NFR-37); this only avoids presenting a form that would
  // 403 on save, after the whole recipe has been retyped.
  if (isEditing && existing && !existing.can_edit) {
    return (
      <div className="mx-auto max-w-3xl">
        <p className="rounded-lg bg-stone-100 px-4 py-3 text-sm dark:bg-stone-800">
          {fr.ownership.cannotEdit}
        </p>
        <Button variant="link" onClick={() => void navigate(`/recipes/${recipeId}`)}>
          {fr.recipeDetail.back}
        </Button>
      </div>
    )
  }

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className="mx-auto flex max-w-3xl flex-col gap-6 pb-24"
    >
      <h1 className="text-2xl font-semibold tracking-tight">
        {isEditing ? fr.recipeEdit.editHeading : fr.recipeEdit.newHeading}
      </h1>

      {saveError && (
        <p
          role="alert"
          className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-300"
        >
          {saveError}
        </p>
      )}

      <PhotoField
        current={existing?.photo ?? null}
        picked={photoFile}
        onPick={(file) => {
          setPhotoFile(file)
          // Picking a new file supersedes a pending removal.
          if (file) setPhotoRemoved(false)
        }}
        removed={photoRemoved}
        onRemove={() => setPhotoRemoved(true)}
      />

      {/* One continuous column, in the order a recipe is actually described: what it is,
          then how much and how long, then what goes in it and what to do, then where it
          came from. Required fields carry a red asterisk and optional ones say so, which is
          the part of F-15 that worked — the two labelled blocks it used to be split into
          separated fields that belong next to each other. */}
      <Field
        name="title"
        label={fr.recipeEdit.titleLabel}
        error={errors.title}
        required
        htmlFor="title"
      >
        <Input
          id="title"
          aria-required
          aria-invalid={Boolean(errors.title)}
          value={form.title}
          onChange={(event) => set('title', event.target.value)}
        />
      </Field>

      <Field name="description" label={fr.recipeEdit.descriptionLabel} htmlFor="description">
        <Textarea
          id="description"
          rows={3}
          value={form.description}
          onChange={(event) => set('description', event.target.value)}
        />
      </Field>

      <Field name="tags" label={fr.recipeEdit.tagsLabel}>
        <TagPicker tags={form.tags} onChange={(tags) => set('tags', tags)} />
      </Field>

      {/* Three number inputs side by side do not fit 375px with their labels, so they
          stack until there is room (invariant 9). Nothing hangs below any one of them:
          a hint under the third pushed it out of line with the other two. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field
          name="servings"
          label={fr.recipeEdit.servingsLabel}
          error={errors.servings}
          required
          htmlFor="servings"
        >
          <Input
            id="servings"
            type="number"
            inputMode="numeric"
            min={1}
            aria-required
            aria-invalid={Boolean(errors.servings)}
            value={form.servings}
            onChange={(event) => set('servings', event.target.value)}
          />
        </Field>
        <Field
          name="prep_time_min"
          label={fr.recipeEdit.prepTimeLabel}
          error={errors.prep_time_min}
          required
          htmlFor="prep_time_min"
        >
          <Input
            id="prep_time_min"
            type="number"
            inputMode="numeric"
            min={0}
            aria-required
            aria-invalid={Boolean(errors.prep_time_min)}
            value={form.prep_time_min}
            onChange={(event) => set('prep_time_min', event.target.value)}
          />
        </Field>
        <Field
          name="cook_time_min"
          label={fr.recipeEdit.cookTimeLabel}
          error={errors.cook_time_min}
          required
          htmlFor="cook_time_min"
        >
          <Input
            id="cook_time_min"
            type="number"
            inputMode="numeric"
            min={0}
            aria-required
            aria-invalid={Boolean(errors.cook_time_min)}
            value={form.cook_time_min}
            onChange={(event) => set('cook_time_min', event.target.value)}
          />
        </Field>
      </div>

      <Field
        name="ingredients"
        label={fr.recipeEdit.ingredientsHeading}
        error={errors.ingredients}
        required
        as="h2"
      >
        <IngredientEditor
          rows={form.ingredients}
          units={units ?? []}
          catalog={catalog ?? []}
          onChange={(rows) => set('ingredients', rows)}
        />
      </Field>

      <Field
        name="steps"
        label={fr.recipeEdit.stepsHeading}
        error={errors.steps}
        required
        as="h2"
      >
        <StepEditor rows={form.steps} onChange={(rows) => set('steps', rows)} />
      </Field>

      <Field name="source" label={fr.recipeEdit.sourceLabel} htmlFor="source">
        <Input
          id="source"
          value={form.source}
          onChange={(event) => set('source', event.target.value)}
        />
      </Field>

      <Field name="notes" label={fr.recipeEdit.notesLabel} htmlFor="notes">
        <Textarea
          id="notes"
          rows={3}
          value={form.notes}
          onChange={(event) => set('notes', event.target.value)}
        />
      </Field>

      <div className="fixed inset-x-0 bottom-0 border-t border-stone-200 bg-white/95 p-4 pb-safe-b backdrop-blur dark:border-stone-800 dark:bg-stone-950/95 sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:pb-0 sm:backdrop-blur-none">
        <div className="mx-auto flex max-w-3xl justify-end gap-2">
          <Button type="submit" disabled={isSaving}>
            {isSaving ? fr.common.saving : fr.common.save}
          </Button>
        </div>
      </div>
    </form>
  )
}

/** One labelled field, with its required marker and error message in one place. */
function Field({
  name,
  label,
  error,
  required,
  htmlFor,
  as = 'label',
  children,
}: {
  name: string
  label: string
  error?: string
  required?: boolean
  htmlFor?: string
  as?: 'label' | 'h2'
  children: React.ReactNode
}) {
  const heading =
    as === 'h2' ? (
      <h2 className="text-base font-semibold">
        {label}
        {required && <RequiredMark />}
      </h2>
    ) : (
      <Label htmlFor={htmlFor}>
        {label}
        {required ? <RequiredMark /> : <OptionalSuffix />}
      </Label>
    )

  return (
    <div data-field={name} className="flex scroll-mt-20 flex-col gap-1.5">
      {heading}
      {children}
      {error && (
        <p role="alert" className="text-sm text-red-700 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  )
}

function RequiredMark() {
  return (
    <span aria-label={fr.recipeEdit.requiredMark} className="ml-1 text-red-600 dark:text-red-400">
      *
    </span>
  )
}

function OptionalSuffix() {
  return (
    <span className="ml-1 font-normal text-stone-400">{fr.recipeEdit.optionalSuffix}</span>
  )
}

/**
 * DRF answers a 400 with `{field: [messages]}`. Flattening it to one line beats showing
 * nothing, and beats a raw JSON dump — the per-field messages the form itself produces are
 * the primary path, and this is what catches whatever the form did not anticipate.
 */
function describeSaveError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return fr.ownership.cannotEdit
    if (error.body && typeof error.body === 'object') {
      const messages = Object.values(error.body as Record<string, unknown>)
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .map(String)
      if (messages.length > 0) return messages.join(' ')
    }
    return error.message
  }
  return fr.recipeEdit.validation.saveFailed
}
